import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Brain, 
  Zap, 
  LayoutDashboard, 
  Mail, 
  Upload,
  Users,
  CheckCircle2,
  ArrowRight,
  Briefcase,
  Home,
  Scale,
  Star,
  Clock,
  FileText,
  Calendar,
  Eye,
  Settings,
  ChevronRight
} from "lucide-react";

// Analytics helper
const trackCTAClick = (cta, location) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'compare_page_cta_click', {
      page: 'heyintake-vs-intakeq-vs-jotform',
      cta: cta,
      location: location
    });
  }
};

// FAQ Schema data
const faqData = [
  {
    question: "How do AI scores work?",
    answer: "HeyIntake analyzes each submission using AI to evaluate lead quality and intake completeness. You get a score (1-10) plus a plain-English explanation of why each lead scored the way it did. This helps you prioritize follow-ups instantly."
  },
  {
    question: "Can I migrate existing forms?",
    answer: "Yes. You can recreate your existing form fields in HeyIntake's form builder in minutes. If you have a large volume of historical data, contact us and we can discuss import options."
  },
  {
    question: "Can I embed forms on my site?",
    answer: "Absolutely. Every HeyIntake form gets a shareable link and an embed code you can drop into any website, landing page, or CMS."
  },
  {
    question: "Can multiple teammates use it?",
    answer: "Yes. Professional plans include team collaboration features. You can invite team members to view submissions, manage the pipeline, and follow up with leads."
  },
  {
    question: "What happens after the trial?",
    answer: "After your 7-day trial, you'll be charged for your selected plan (Starter at $19/mo or Professional at $49/mo). You can cancel anytime before the trial ends with no charge."
  },
  {
    question: "Is HeyIntake HIPAA compliant?",
    answer: "HeyIntake is not positioned as a HIPAA-first platform. If you require strict HIPAA compliance for protected health information, we recommend evaluating HIPAA-specific tools designed for healthcare intake workflows."
  },
  {
    question: "Do I need technical skills to set it up?",
    answer: "No. HeyIntake is designed for non-technical users. You can create forms, embed them, and start receiving AI-scored submissions within minutes—no coding required."
  },
  {
    question: "Can I export my data?",
    answer: "Yes. Professional plans include CSV export for leads and contacts. You maintain full ownership of your data."
  }
];

export default function HeyIntakeVsIntakeQVsJotform() {
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  // Comparison table data
  const comparisonRows = [
    { 
      feature: "Best for", 
      heyintake: "Service businesses that need to qualify leads fast", 
      intakeq: "Healthcare/clinic-style intake workflows", 
      jotform: "General-purpose form building across industries" 
    },
    { 
      feature: "Speed to value", 
      heyintake: "Minutes — AI scoring works immediately", 
      intakeq: "Varies by setup complexity", 
      jotform: "Varies — depends on integrations needed" 
    },
    { 
      feature: "AI lead scoring", 
      heyintake: "Built-in, automatic", 
      intakeq: "Not a core feature", 
      jotform: "Requires third-party integrations" 
    },
    { 
      feature: "AI intake scoring", 
      heyintake: "Built-in, automatic", 
      intakeq: "Not a core feature", 
      jotform: "Requires third-party integrations" 
    },
    { 
      feature: "Score explanations", 
      heyintake: "Plain-English AI summaries", 
      intakeq: "N/A", 
      jotform: "N/A" 
    },
    { 
      feature: "Lead → Intake workflow", 
      heyintake: "Native pipeline with stages", 
      intakeq: "Depends on setup", 
      jotform: "Requires external CRM" 
    },
    { 
      feature: "Follow-up emailing", 
      heyintake: "Send emails from inside the app", 
      intakeq: "Varies by plan", 
      jotform: "Limited — requires integrations" 
    },
    { 
      feature: "Contacts import/export", 
      heyintake: "CSV import & export included", 
      intakeq: "Varies", 
      jotform: "Varies by plan" 
    },
    { 
      feature: "Scheduling support", 
      heyintake: "Link scheduling tools in follow-up emails", 
      intakeq: "Built-in scheduling features", 
      jotform: "Via integrations" 
    },
    { 
      feature: "File collection", 
      heyintake: "Upload links to Google Drive/Dropbox in forms", 
      intakeq: "Direct file uploads", 
      jotform: "Direct file uploads" 
    },
    { 
      feature: "Team collaboration", 
      heyintake: "Included in Professional plan", 
      intakeq: "Varies by plan", 
      jotform: "Varies by plan" 
    },
    { 
      feature: "Submission quality visibility", 
      heyintake: "AI scores + priority sorting", 
      intakeq: "Manual review", 
      jotform: "Manual review" 
    },
    { 
      feature: "Setup simplicity", 
      heyintake: "No-code, 5-minute setup", 
      intakeq: "Moderate learning curve", 
      jotform: "Easy form builder, complex for automation" 
    }
  ];

  return (
    <>
      <Helmet>
        <title>HeyIntake vs IntakeQ vs Jotform (2026 Comparison)</title>
        <meta name="description" content="Compare HeyIntake, IntakeQ, and Jotform for service business intake. See which tool helps you qualify leads faster with AI scoring. Start your 7-day trial." />
        <link rel="canonical" href="https://heyintake.com/compare/heyintake-vs-intakeq-vs-jotform" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqData.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })
        }} />
      </Helmet>

      <div className="min-h-screen bg-[#f5f5f0]">
        {/* Navigation */}
        <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-foreground">HeyIntake</span>
              </Link>
              <div className="hidden md:flex items-center gap-4">
                <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground">Pricing</Link>
                <Link to="/demo" className="text-sm text-muted-foreground hover:text-foreground">Demo</Link>
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Login</Link>
                <Link to="/register">
                  <Button size="sm" onClick={() => trackCTAClick('trial', 'nav')}>Start Trial</Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div {...fadeIn}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                HeyIntake vs IntakeQ vs Jotform
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                If you're a service business drowning in low-quality leads, HeyIntake helps you qualify faster with AI lead + intake scoring.
              </p>
              
              {/* CTA Row */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                <Link to="/register" onClick={() => trackCTAClick('trial', 'hero')}>
                  <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6">
                    Start 7-day free trial
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/demo" onClick={() => trackCTAClick('demo', 'hero')}>
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6">
                    Book a demo
                  </Button>
                </Link>
              </div>

              {/* Benefit Bullets */}
              <div className="flex flex-col md:flex-row gap-4 md:gap-8 justify-center mb-12 text-left md:text-center">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium">Instantly spot your best leads</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium">Turn submissions into clear next steps</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium">Less admin. More booked work.</span>
                </div>
              </div>

              {/* Proof Bar */}
              <div className="flex flex-wrap justify-center gap-4 md:gap-8">
                {[
                  { icon: <Brain className="w-5 h-5" />, label: "AI Lead Scoring" },
                  { icon: <FileText className="w-5 h-5" />, label: "AI Intake Scoring" },
                  { icon: <LayoutDashboard className="w-5 h-5" />, label: "Light CRM Pipeline" },
                  { icon: <Mail className="w-5 h-5" />, label: "Email + Import/Export" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-4 py-2 bg-muted/50 rounded-full">
                    <span className="text-primary">{item.icon}</span>
                    <span className="text-sm font-medium text-muted-foreground">{item.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section A: The 20-second decision */}
        <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div {...fadeIn} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                The 20-second decision
              </h2>
              <p className="text-muted-foreground">Which tool fits your workflow?</p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* HeyIntake Card */}
              <motion.div {...fadeIn}>
                <Card className="h-full border-2 border-primary bg-white shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                        <Zap className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-xl font-bold">HeyIntake</span>
                      <Badge className="ml-auto">Recommended</Badge>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-foreground mb-1">Best for:</p>
                        <p className="text-sm text-muted-foreground">Service businesses that want AI to instantly qualify and prioritize every lead.</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground mb-1">Not ideal if:</p>
                        <p className="text-sm text-muted-foreground">You need a full-featured healthcare EMR or complex multi-step clinical workflows.</p>
                      </div>
                    </div>
                    <Link to="/register" onClick={() => trackCTAClick('trial', 'mid')} className="block mt-6">
                      <Button className="w-full">Start trial <ArrowRight className="ml-2 w-4 h-4" /></Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>

              {/* IntakeQ Card */}
              <motion.div {...fadeIn} transition={{ delay: 0.1 }}>
                <Card className="h-full bg-white/70">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-gray-500" />
                      </div>
                      <span className="text-xl font-bold text-muted-foreground">IntakeQ</span>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-foreground mb-1">Best for:</p>
                        <p className="text-sm text-muted-foreground">Healthcare and clinic-style intake workflows with scheduling needs.</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground mb-1">Not ideal if:</p>
                        <p className="text-sm text-muted-foreground">You're a general service business wanting AI-powered lead qualification.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Jotform Card */}
              <motion.div {...fadeIn} transition={{ delay: 0.2 }}>
                <Card className="h-full bg-white/70">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Settings className="w-6 h-6 text-gray-500" />
                      </div>
                      <span className="text-xl font-bold text-muted-foreground">Jotform</span>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-foreground mb-1">Best for:</p>
                        <p className="text-sm text-muted-foreground">General-purpose form building with thousands of templates across industries.</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground mb-1">Not ideal if:</p>
                        <p className="text-sm text-muted-foreground">You want built-in lead scoring and a pipeline without complex integrations.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Section B: Feature Comparison Table */}
        <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <motion.div {...fadeIn} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Feature comparison
              </h2>
              <p className="text-muted-foreground">See how HeyIntake stacks up</p>
            </motion.div>

            <motion.div {...fadeIn} className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-4 bg-muted/30 font-semibold text-foreground">Feature</th>
                    <th className="text-left p-4 bg-primary/10 font-semibold text-primary border-l-4 border-primary">
                      <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        HeyIntake
                      </div>
                    </th>
                    <th className="text-left p-4 bg-muted/30 font-semibold text-muted-foreground">IntakeQ</th>
                    <th className="text-left p-4 bg-muted/30 font-semibold text-muted-foreground">Jotform</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-muted/10'}>
                      <td className="p-4 font-medium text-foreground">{row.feature}</td>
                      <td className="p-4 bg-primary/5 border-l-4 border-primary text-foreground font-medium">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span>{row.heyintake}</span>
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">{row.intakeq}</td>
                      <td className="p-4 text-muted-foreground">{row.jotform}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>

            <p className="text-xs text-muted-foreground text-center mt-6">
              Competitor positioning is based on public product information and may change.
            </p>

            <div className="text-center mt-8">
              <Link to="/register" onClick={() => trackCTAClick('trial', 'mid')}>
                <Button size="lg">Start your trial <ArrowRight className="ml-2 w-4 h-4" /></Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Section C: Why HeyIntake wins */}
        <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div {...fadeIn} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Why HeyIntake wins for service businesses
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  icon: <Brain className="w-8 h-8" />,
                  title: "Stop guessing who's worth your time",
                  description: "AI lead scoring analyzes every submission instantly. You see a score and explanation before you even open the details. High-value leads get your attention first."
                },
                {
                  icon: <FileText className="w-8 h-8" />,
                  title: "Get the details you need, then prioritize",
                  description: "AI intake scoring evaluates how complete and qualified each submission is. No more manual review to figure out who's ready to move forward."
                },
                {
                  icon: <LayoutDashboard className="w-8 h-8" />,
                  title: "Your submissions don't disappear in a spreadsheet",
                  description: "Every lead flows into a visual pipeline. Drag and drop through stages. See your entire intake funnel at a glance — no external CRM required."
                },
                {
                  icon: <Mail className="w-8 h-8" />,
                  title: "Turn intake into action",
                  description: "Email leads directly from HeyIntake. Drop in your scheduling link for follow-ups. Collect files via Google Drive or Dropbox links in your forms. One tool, complete workflow."
                }
              ].map((block, i) => (
                <motion.div 
                  key={i}
                  {...fadeIn}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card className="h-full bg-white">
                    <CardContent className="p-6">
                      <div className="text-primary mb-4">{block.icon}</div>
                      <h3 className="text-xl font-bold text-foreground mb-3">{block.title}</h3>
                      <p className="text-muted-foreground mb-4">{block.description}</p>
                      <Link 
                        to="/register" 
                        onClick={() => trackCTAClick('trial', 'mid')}
                        className="text-primary font-medium inline-flex items-center hover:underline"
                      >
                        Start trial <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Section D: Use-case examples */}
        <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <motion.div {...fadeIn} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                How service businesses use HeyIntake
              </h2>
              <p className="text-muted-foreground">Example outcomes from typical workflows</p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: <Scale className="w-8 h-8" />,
                  title: "Law firm",
                  subtitle: "Spot high-value matters fast",
                  description: "A personal injury firm receives 50+ inquiries weekly. AI scoring immediately flags cases with strong liability indicators and high potential value. Partners focus on the top 20% instead of reviewing every intake."
                },
                {
                  icon: <Home className="w-8 h-8" />,
                  title: "Home services",
                  subtitle: "Prioritize urgent jobs",
                  description: "An HVAC company gets flooded with requests. AI scoring surfaces emergency repairs and high-budget projects. Dispatchers route technicians to the most valuable calls first."
                },
                {
                  icon: <Briefcase className="w-8 h-8" />,
                  title: "Consulting",
                  subtitle: "Qualify serious prospects",
                  description: "A business consultant receives dozens of discovery requests. AI intake scoring identifies prospects with budget, timeline, and decision-making authority. No more wasted calls with tire-kickers."
                }
              ].map((useCase, i) => (
                <motion.div 
                  key={i}
                  {...fadeIn}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card className="h-full">
                    <CardContent className="p-6">
                      <div className="text-primary mb-4">{useCase.icon}</div>
                      <p className="text-sm font-medium text-primary mb-1">{useCase.title}</p>
                      <h3 className="text-lg font-bold text-foreground mb-3">{useCase.subtitle}</h3>
                      <p className="text-sm text-muted-foreground">{useCase.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Section E: Objection handling */}
        <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <motion.div {...fadeIn} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Common questions before switching
              </h2>
            </motion.div>

            <motion.div {...fadeIn}>
              <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm">
                <AccordionItem value="item-1" className="border-b">
                  <AccordionTrigger className="px-6 py-4 text-left font-semibold">
                    I already use Jotform — why switch?
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-muted-foreground">
                    Jotform is great for collecting data. HeyIntake goes further: it scores every submission with AI, so you know which leads deserve your time before you even open them. If you're manually sorting through submissions to find the good ones, you're doing work AI can do instantly.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2" className="border-b">
                  <AccordionTrigger className="px-6 py-4 text-left font-semibold">
                    I just need something simple
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-muted-foreground">
                    HeyIntake is simple. Create a form in minutes, embed it anywhere, and let AI handle the qualification. No complex setup. No integrations to configure. Simple doesn't mean basic — it means you get powerful results without the overhead.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3" className="border-b">
                  <AccordionTrigger className="px-6 py-4 text-left font-semibold">
                    How accurate are AI scores?
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-muted-foreground">
                    AI scores are based on the information submitted and your form's context. They're designed to give you a fast, directional signal — not replace your judgment. Most users find scores align with their own assessment after reviewing a few submissions. You always see the reasoning behind each score.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4" className="border-b">
                  <AccordionTrigger className="px-6 py-4 text-left font-semibold">
                    Will this replace my CRM?
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-muted-foreground">
                    HeyIntake includes a light CRM pipeline for managing leads through intake stages. For many service businesses, it's all you need. If you have an existing CRM you love, you can export leads via CSV and continue your workflow there.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-5">
                  <AccordionTrigger className="px-6 py-4 text-left font-semibold">
                    Can I keep my existing workflow?
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-muted-foreground">
                    Yes. Embed HeyIntake forms on your existing site, link your scheduling tool in follow-up emails, and export data when needed. HeyIntake adapts to your workflow — you don't have to rebuild everything around a new tool.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </motion.div>
          </div>
        </section>

        {/* Section F: FAQ */}
        <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-3xl mx-auto">
            <motion.div {...fadeIn} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Frequently asked questions
              </h2>
            </motion.div>

            <motion.div {...fadeIn}>
              <Accordion type="single" collapsible className="bg-muted/20 rounded-lg">
                {faqData.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-b last:border-b-0">
                    <AccordionTrigger className="px-6 py-4 text-left font-semibold">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>

            <div className="mt-8 text-center">
              <p className="text-muted-foreground mb-4">Have more questions?</p>
              <Link to="/faq" className="text-primary font-medium hover:underline">
                Visit our full FAQ page <ChevronRight className="w-4 h-4 inline" />
              </Link>
            </div>
          </div>
        </section>

        {/* Section G: Final CTA */}
        <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-primary text-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div {...fadeIn}>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Try HeyIntake for 7 days for $1
              </h2>
              <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
                See your best leads immediately — and stop wasting time on low-quality submissions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register" onClick={() => trackCTAClick('trial', 'footer')}>
                  <Button size="lg" variant="secondary" className="w-full sm:w-auto text-lg px-8 py-6">
                    Start 7-day free trial
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/demo" onClick={() => trackCTAClick('demo', 'footer')}>
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 border-white text-white hover:bg-white/10">
                    Book a demo
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-white/60 mt-6">
                No credit card required to explore. Cancel anytime.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer Links */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-t">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <Link to="/pricing" className="hover:text-foreground">Pricing</Link>
              <Link to="/demo" className="hover:text-foreground">Demo</Link>
              <Link to="/faq" className="hover:text-foreground">FAQ</Link>
              <Link to="/intake-form-for-lawyers" className="hover:text-foreground">For Lawyers</Link>
              <Link to="/intake-form-for-consultants" className="hover:text-foreground">For Consultants</Link>
              <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground">Terms</Link>
            </div>
            <p className="text-center text-xs text-muted-foreground mt-6">
              © {new Date().getFullYear()} HeyIntake. All rights reserved.
            </p>
          </div>
        </section>

        {/* Sticky Mobile CTA Bar */}
        {showStickyBar && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 md:hidden z-50">
            <div className="flex gap-3">
              <Link to="/register" onClick={() => trackCTAClick('trial', 'sticky')} className="flex-1">
                <Button className="w-full">Start trial</Button>
              </Link>
              <Link to="/demo" onClick={() => trackCTAClick('demo', 'sticky')} className="flex-1">
                <Button variant="outline" className="w-full">Demo</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
